// What's new since the last visit (README "What's new"). tools/seo/seo.mjs publishes the latest lines of the What's new
// page as site/whats-new/news.json, { lines: [{ id, date, type, text }] } newest first, each id a hash of its text. This
// browser keeps what it has read as { day, ids }: the newest day it saw and which of that day's lines, so a line added
// to a day already read still counts as new, and a reworded older line doesn't. site/js/app.js reads the file and a
// pigeon brings the unread lines (site/js/ui/pigeon.js).

export const NEWS_CHECK_MS = 10 * 60 * 1000;    // at most one read of news.json per open tab in this long
const DAY = /^\d{4}-\d{2}-\d{2}$/;

// What kind of update each NEWS line is (tools/seo/seo-content.mjs), most notable first: the pigeon's badge names the
// first kind it brings (`badge`, in the sign font), and the card, the About tab and whats-new/ tag every line with its
// `label`. `colour` paints the badge; site/css/app.css and site/css/page.css give the tags the same ones.
export const NEWS_TYPES = {
  city: { label: 'New city', badge: 'NEW CITY', colour: '#f6c177' },
  feature: { label: 'New feature', badge: 'NEW', colour: '#7ee0b5' },
  improved: { label: 'Improved', badge: 'IMPROVED', colour: '#8ab8ff' },
  fixed: { label: 'Fixed', badge: 'FIXED', colour: '#f2a0c0' },
};
const kind = type => (Object.hasOwn(NEWS_TYPES, type) ? type : null);

/** A line's thank-you to the supporter whose coffee brought it, after its text everywhere it shows: "Thanks to Ana". */
export const thanksTo = name => `Thanks to ${name}`;

/**
 * The lines of a news.json body, newest first, anything malformed left out; a type this page doesn't know is null, and
 * `thanks` (the supporter's name) is there only when the line has one.
 */
export function newsLines(json) {
  if (!Array.isArray(json?.lines)) return [];
  return json.lines
    .filter(l => l && typeof l.id === 'string' && l.id && DAY.test(l.date) && typeof l.text === 'string' && l.text)
    .map(l => ({ id: l.id, date: l.date, type: kind(l.type), text: l.text, ...(typeof l.thanks === 'string' && l.thanks && { thanks: l.thanks }) }));
}

/** The most notable kind of update among `lines` (the order of NEWS_TYPES), or null when none has a known one. */
export const topType = lines => Object.keys(NEWS_TYPES).find(t => lines.some(l => l.type === t)) ?? null;

/** What reading every one of `lines` leaves behind: { day, ids }, or null for no lines. */
export function seenOf(lines) {
  const day = lines[0]?.date;
  return day ? { day, ids: lines.filter(l => l.date === day).map(l => l.id) } : null;
}

/**
 * What this browser has read, for `lines`: what it `stored` when it is well-formed; for a `returning` browser with
 * nothing stored (it came before the pigeon did), none of the newest day's lines; else null, a first visit, which only
 * notes what there is (seenOf).
 */
export function seenNow(stored, lines, returning) {
  if (stored && typeof stored.day === 'string' && DAY.test(stored.day) && Array.isArray(stored.ids)) return stored;
  return returning && lines.length ? { day: lines[0].date, ids: [] } : null;
}

/** The lines not read yet: those of a newer day than the one `seen` last, and that day's lines it didn't have. */
export function unseenLines(lines, seen) {
  if (!seen) return [];
  return lines.filter(l => l.date > seen.day || (l.date === seen.day && !seen.ids.includes(l.id)));
}

/** Whether any of `unseen` went live after the page read `boot` (the ids of its first news.json): a deploy it lacks. */
export const cameLater = (unseen, boot) => unseen.some(l => !boot.has(l.id));

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 2026-09-29 → "29 Sep", as the About tab writes a day */
export const shortDay = day => `${+day.slice(8)} ${MONTHS[day.slice(5, 7) - 1]}`;

/** `lines` (newest first) as days, newest first: [{ date, lines: [line, …] }], for the card's day headings. */
export function byDay(lines) {
  const days = [];
  for (const line of lines) {
    if (days.at(-1)?.date === line.date) days.at(-1).lines.push(line);
    else days.push({ date: line.date, lines: [line] });
  }
  return days;
}
